import * as vp from 'vopay';
import { Box, Table, Pagination, DropDown, usePagination, useUpdateQueryParams } from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import useFetchAccountInvoices from '@/hooks/useApi/useFetchAccountInvoices';
import useFetchAccountStatementCsv from '@/hooks/useApi/useFetchAccountStatementCsv';
import useFetchAccountStatementPDF from '@/hooks/useApi/useFetchAccountStatementPDF';
import useFetchAccountInvoicePdf from '@/hooks/useApi/useFetchAccountInvoicePdf';
import useFileDownload from '@/hooks/useFiledDownload';
import InvoicesSearchForm from './InvoicesSearchForm';
import InvoicesContext, { InvoicesCtx } from '../Dialogue/Invoices/InvoicesContext';
import InvoicesDialogue from '../Dialogue/Invoices/InvoicesDialogue';
import { InvoicesDialogueHandler, DialogueHandlerProps } from '../Dialogue/types';
import styles from './Invoices.module.scss';

import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useTranslation } from 'react-i18next';

import { Button, TableFiltersContainer } from 'UI';

type QueryParams = {
  searchTerm: string;
};

type InvoicesSearchParams = {
  searchTerm?: string;
};

type FC = FunctionComponent;
type DropdownButtonProps = {
  index: number;
  invoice: vp.PartnerInvoice;
};

const Invoices: FC = () => {
  const [invoicesResponse, , , fetchInvoices] = useFetchAccountInvoices({});
  const [statementsResponse, fetchStatement] = useFetchAccountStatementCsv({});
  const [statementsPDFResponse, fetchStatementPDF] = useFetchAccountStatementPDF({});
  const [invoicePDFResponse, fetchInvoicePdf] = useFetchAccountInvoicePdf({});
  const [filteredList, setFilteredList] = useState<vp.PartnerInvoice[]>([]);
  const { downloadCsvFileType, downloadPdfFileType } = useFileDownload();
  const [showDownloadErrorAlert, setShowDownloadErrorAlert] = useState(false);

  const alertDialog = useAlertDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountAndBilling.InvoicesAndStatements' });

  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      searchTerm: params.get('searchTerm') || '',
    };
  }, [location.search]);

  const searchTerm = useMemo(() => queryParams.searchTerm || '', [queryParams]);
  const filtersApplied = useMemo(() => {
    return Object.values(queryParams).some((val) => val !== '');
  }, [queryParams]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  // pagination
  const totalDataLength = useMemo(() => {
    return Object.values(filteredList).length || 0;
  }, [filteredList]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, offset, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const search = async (searchParams: InvoicesSearchParams = {}) => {
    const defaultValues = {
      SearchText: searchParams.searchTerm ?? searchTerm,
    };
    try {
      const res = await fetchInvoices(defaultValues);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const approvalTransactionRequest = Object.values(res.Invoices || {});
      setFilteredList(approvalTransactionRequest);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('InvoiceSearchForm.fetchErrorMessage');
      alertDialog.error('Error', msg);
      console.error(err);
    }
  };

  const invoicesContext: InvoicesCtx = {
    showDownloadErrorAlert,
  };

  useEffect(() => {
    setPageIndex(0);
    const searchParams = {
      searchTerm,
    };
    updateQueryParams(searchParams);
    search(searchParams);
  }, []);

  const downloadInvoice = (invoiceId: string) => {
    fetchInvoicePdf({
      invoiceId,
    });
  };
  const downloadStatement = (invoiceId: string) => {
    fetchStatement({
      invoiceId,
    });
  };
  const downloadStatementPDF = (invoiceId: string) => {
    fetchStatementPDF({
      invoiceId,
    });
  };
  function DropdownTable({ index, invoice }: DropdownButtonProps) {
    const [isMenuVisible, setIsMenuVisible] = useState(false);

    return (
      <div className={styles.dropdownContainer}>
        <Button
          id={`download-statements-${index}`}
          size="small"
          iconRight="keyboard_arrow_down"
          onClick={() => {
            setIsMenuVisible(!isMenuVisible);
          }}
        >
          {t('Table.DownloadStatementDropdown.title')}
        </Button>
        <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
          <Button
            id="export-PDF-btn"
            size="small"
            onClick={() => {
              downloadStatementPDF(invoice.InvoiceID);
            }}
            style={{ border: 'none', width: '100%' }}
          >
            {t('Table.DownloadStatementDropdown.pdf')}
          </Button>
          <Button
            id="export-csv-btn"
            size="small"
            onClick={() => {
              downloadStatement(invoice.InvoiceID);
            }}
            style={{ border: 'none', width: '100%' }}
          >
            {t('Table.DownloadStatementDropdown.csv')}
          </Button>
        </DropDown>
      </div>
    );
  }
  useEffect(() => {
    if (statementsResponse) {
      if (statementsResponse.success) {
        downloadCsvFileType(statementsResponse.statementCsv, 'statement.csv');
      } else {
        setShowDownloadErrorAlert(true);
      }
    }
  }, [statementsResponse]);
  useEffect(() => {
    if (statementsPDFResponse) {
      if (statementsPDFResponse.success) {
        downloadPdfFileType(statementsPDFResponse.statementPDF, 'statement.pdf');
      } else {
        setShowDownloadErrorAlert(true);
      }
    }
  }, [statementsPDFResponse]);
  useEffect(() => {
    if (invoicePDFResponse) {
      if (invoicePDFResponse.success) {
        downloadPdfFileType(invoicePDFResponse.invoicePdf, 'invoice.pdf');
      } else {
        setShowDownloadErrorAlert(true);
      }
    }
  }, [invoicePDFResponse]);
  useEffect(() => {
    if (invoicesResponse) {
      setFilteredList(invoicesResponse?.accountInvoices || []);
    }
  }, [invoicesResponse]);

  const handlers = {
    handleInvoicesDialogue: (handlerProps: DialogueHandlerProps<InvoicesDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: InvoicesDialogueHandler = {
        confirm: {
          confirm: {
            default: () => {
              setShowDownloadErrorAlert(false);
            },
          },
          fail: {
            default: () => {},
          },
        },
      };
      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };
  const tableProps: vp.TableProps<vp.PartnerInvoice & { index: number }> = {
    id: 'transaction-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'DateIssued',
        header: t('Table.dateIssued'),
        align: 'left',
        field: 'InvoiceDate',
      },
      {
        id: 'InvoiceNumber',
        header: t('Table.invoiceNumber'),
        align: 'left',
        field: 'InvoiceNumber',
      },
      {
        id: 'Amount',
        header: t('Table.amount'),
        align: 'left',
        template: ({ data }) => vp.getCurrencySign(data.CurrencyShortForm) + vp.formattedCurrency(data.Total),
      },
      {
        id: 'Currency',
        header: t('Table.currency'),
        align: 'left',
        field: 'CurrencyShortForm',
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            <Button
              id={`download-statments-${data.index}`}
              size="small"
              onClick={() => {
                downloadInvoice(data.InvoiceID);
              }}
            >
              {t('Table.downloadInvoicePDF')}
            </Button>
            <DropdownTable index={data.index} invoice={data} />
          </Box>
        ),
      },
    ],
    rows: filteredList
      .sort((a, b) => new Date(b.InvoiceDate).getTime() - new Date(a.InvoiceDate).getTime())
      .map((invoice, index) => ({
        id: String(invoice.InvoiceID),
        data: { ...invoice, index },
      }))
      .slice(offset, offset + PER_PAGE),
  };

  return (
    <>
      <InvoicesContext.Provider value={invoicesContext}>
        <Box jc="sb" flx="col">
          <Box flx="col" gap="sm">
            <TableFiltersContainer filtersApplied={filtersApplied}>
              <TableFiltersContainer.Trigger />
              <TableFiltersContainer.Content>
                <InvoicesSearchForm
                  searchTerm={searchTerm}
                  search={(searchParams) => {
                    setPageIndex(0);
                    updateQueryParams({
                      searchTerm: searchParams.searchTerm || '',
                    });
                    search(searchParams);
                  }}
                />
              </TableFiltersContainer.Content>
            </TableFiltersContainer>
          </Box>
          <Box>
            <Table {...tableProps} />
          </Box>
          <Box width="100%" jc="center" mt="lg" mb="sm">
            {pageCount > 1 && (
              <Pagination
                pageCount={pageCount}
                forcePageNumber={pageIndex}
                handlePageClick={(e) => {
                  setPageIndex(e.selected);
                }}
              />
            )}
          </Box>
        </Box>
        <InvoicesDialogue handleDialogue={handlers.handleInvoicesDialogue} />
      </InvoicesContext.Provider>
    </>
  );
};

export default Invoices;
